// Drivers.js
import React, { useState, useEffect } from 'react'; // Keep useEffect for vehicle fetch
import ViewOnlyNotice from './ViewOnlyNotice';
import axios from 'axios';
import { useNotification } from '../context/NotificationContext';
import {
  Typography,
  Box,
  Paper,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Switch,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormGroup,
  InputAdornment,
  TablePagination // Import TablePagination
} from '@mui/material';
import {
  Edit as EditIcon,
  Phone as PhoneIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { canManageTargetUser, getStoredPermissions } from '../utils/permissionStore';
import { PAY_TYPES, normalizeDriverPay, describeDriverPay, canSetDriverPay } from '../utils/driverPay';
import { useOrg } from '../context/OrgContext';

// Mirrors the server's canChangeDutyStatus: the driver themselves, or anyone
// who supervises their account.
const canChangeDutyStatus = (permissions, driver) => {
  if (!permissions) return false;
  const driverId = String(driver._id || driver.id);
  return permissions.userId === driverId || canManageTargetUser(permissions, driver);
};

function Drivers({
  drivers, // Receive drivers from props
  page,
  rowsPerPage,
  totalCount,
  onPaginationChange,
  onSearch, // Accept onSearch, though not directly used here for filtering
  readOnly = false
}) {
  const { showNotification } = useNotification();
  const { name: orgName } = useOrg();
  // Removed local drivers, filteredDrivers, loading, error state
  // Keep state related to dialogs, editing, vehicles etc.
  const [selectedDriver, setSelectedDriver] = useState(null); // Keep if needed for other functionality
  const [openOnDutyConfirmDialog, setOpenOnDutyConfirmDialog] = useState(false);
  const [pendingOnDutyChange, setPendingOnDutyChange] = useState(null);
  // Keep error state for API call feedback (like toggle duty, save)
  const [error, setError] = useState(null);
  // Keep loading state specifically for API calls within this component (toggle duty, save)
  const [loading, setLoading] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [changePassword, setChangePassword] = useState(false);

  // Helper function to get auth headers
  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  // Removed useEffect hook for fetching drivers

  // Toggle on-duty status
  const handleToggleOnDuty = (driverId) => {
    // console.log(`Preparing to toggle on-duty status for user with ID ${driverId}`); // Removed log
    
    // Find the user by either id or _id
    const driver = drivers.find(u => u.id === driverId || u._id === driverId);
    if (!driver) {
      // console.error(`Driver with ID ${driverId} not found`); // Removed log
      return;
    }

    // console.log(`Found driver:`, driver); // Removed log

    // Store the pending on-duty change and show confirmation dialog
    setPendingOnDutyChange({
      driverId,
      driver,
      newValue: !driver.isOnDuty
    });
    setOpenOnDutyConfirmDialog(true);
  };

  // Function to confirm and apply on-duty change
  const confirmOnDutyChange = async () => {
    if (!pendingOnDutyChange) return;
    
    const { driverId, driver, newValue } = pendingOnDutyChange;
    
    try {
      setLoading(true);
      
      // Use _id if available, otherwise use id
      const effectiveUserId = driver._id || driverId;
      // console.log(`Using effective user ID for toggle on-duty: ${effectiveUserId}`); // Removed log
      
      // Try primary API path first
      try {
        // console.log(`Sending PUT request to /api/users/${effectiveUserId}/toggle-on-duty`); // Removed log
        await axios.put(`${process.env.REACT_APP_API_URL}/api/users/${effectiveUserId}/toggle-on-duty`, {}, {
          headers: getAuthHeader()
        });
      } catch (primaryError) {
        // console.error('Error toggling on-duty status with primary API path:', primaryError); // Removed log
        
        // Try alternative API path
        // console.log(`Trying alternative API path: /api/users/${effectiveUserId}/toggle-on-duty`); // Removed log
        await axios.put(`${process.env.REACT_APP_API_URL}/api/users/${effectiveUserId}/toggle-on-duty`, {}, {
          headers: getAuthHeader()
        });
      }
      
      // Removed local state update. Parent component will refetch/update.
      
      // Show success message
      showNotification(`Driver ${newValue ? 'set to On-Duty' : 'set to Off-Duty'} successfully`, 'success');
      setError({ message: `Driver ${newValue ? 'set to On-Duty' : 'set to Off-Duty'} successfully`, severity: 'success' });
      setTimeout(() => setError(null), 3000); // Clear success message after 3 seconds
      
      setLoading(false);
    } catch (error) {
      // console.error('Error toggling on-duty status:', error); // Removed log
      
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to update driver status. Please try again.';
      
      showNotification(errorMessage, 'error');
      setError(errorMessage);
      setLoading(false);
    }
    
    // Close the dialog
    setOpenOnDutyConfirmDialog(false);
    setPendingOnDutyChange(null);
  };

  // Function to cancel on-duty change
  const cancelOnDutyChange = () => {
    setOpenOnDutyConfirmDialog(false);
    setPendingOnDutyChange(null);
  };

  // Effect to load vehicles from localStorage
  useEffect(() => {
    try {
      const cachedVehicles = localStorage.getItem('fleetVehicles');
      if (cachedVehicles) {
        const parsedVehicles = JSON.parse(cachedVehicles);
        // console.log('Drivers - Loaded vehicles from localStorage:', parsedVehicles.length); // Removed log
        setVehicles(parsedVehicles);
      } else {
        // Fetch from API if not in localStorage
        fetchVehicles();
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Error loading vehicles from localStorage';
      showNotification(errorMessage, 'error');
      fetchVehicles();
    }
  }, []);

  // Fetch vehicles from API
  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/settings`, {
        headers: getAuthHeader()
      });
      
      if (response.data && response.data.vehicles) {
        // console.log('Drivers - Fetched vehicles from API:', response.data.vehicles.length); // Removed log
        setVehicles(response.data.vehicles);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Error fetching vehicles';
      showNotification(errorMessage, 'error');
    }
  };

  // Handle opening the edit dialog
  const handleEditDriver = (driver) => {
    setEditingDriver({
      ...driver,
      // Initialize notification preferences if they don't exist
      notifyOnJobAssignment: driver.notifyOnJobAssignment || false,
      notifyOnStatusChange: driver.notifyOnStatusChange || false,
      statusChangeNotificationTypes: driver.statusChangeNotificationTypes || [],
      password: '', // Reset password field for security
      assignedVehicle: driver.assignedVehicle || '',
    });
    setChangePassword(false);
    setShowPassword(false);
    setOpenEditDialog(true);
  };

  // Handle driver form field changes
  const handleDriverFormChange = (field, value) => {
    setEditingDriver(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle address field changes
  const handleAddressChange = (field, value) => {
    setEditingDriver(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value
      }
    }));
  };

  // Handle notification type checkbox changes
  const handleNotificationTypeChange = (type, checked) => {
    setEditingDriver(prev => {
      // Initialize the array if it doesn't exist
      const currentTypes = prev.statusChangeNotificationTypes || [];
      
      if (checked) {
        // Add the type if it's not already in the array
        return {
          ...prev,
          statusChangeNotificationTypes: [...new Set([...currentTypes, type])]
        };
      } else {
        // Remove the type
        return {
          ...prev,
          statusChangeNotificationTypes: currentTypes.filter(t => t !== type)
        };
      }
    });
  };

  // Save driver changes
  const handleSaveDriver = async () => {
    if (!editingDriver) return;
    
    setSaveLoading(true);
    
    try {
      const effectiveUserId = editingDriver._id || editingDriver.id;
      const userData = {
        username: editingDriver.username,
        address: editingDriver.address,
        notifyOnJobAssignment: editingDriver.notifyOnJobAssignment,
        notifyOnStatusChange: editingDriver.notifyOnStatusChange,
        statusChangeNotificationTypes: editingDriver.statusChangeNotificationTypes,
        assignedVehicle: editingDriver.assignedVehicle,
        isActive: editingDriver.isActive,
        driverPay: normalizeDriverPay(editingDriver.driverPay)
      };
      
      // Only include password if we're changing it
      if (changePassword && editingDriver.password) {
        userData.password = editingDriver.password;
      }
      
      // console.log(`Updating driver ${effectiveUserId}:`, userData); // Removed log
      
      // Send the update
      let response;
      try {
        response = await axios.put(`${process.env.REACT_APP_API_URL}/api/users/${effectiveUserId}`, userData, {
          headers: getAuthHeader()
        });
      } catch (primaryError) {
        // console.error('Error updating user with primary API path:', primaryError); // Removed log
        
        // Try alternative API path
        response = await axios.put(`${process.env.REACT_APP_API_URL}/api/users/${effectiveUserId}`, userData, {
          headers: getAuthHeader()
        });
      }
      
      // Removed local state update. Parent component will refetch/update.
      
      // Show success message
      showNotification('Driver updated successfully', 'success');
      setError({ message: 'Driver updated successfully', severity: 'success' });
      setTimeout(() => setError(null), 3000);
      
      // Close the dialog
      setOpenEditDialog(false);
    } catch (error) {
      // console.error('Error updating driver:', error); // Removed log
      
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to update driver. Please try again.';
      
      showNotification(errorMessage, 'error');
      setError(errorMessage);
    } finally {
      setSaveLoading(false);
    }
  };

  // Handle toggling active status
  const handleToggleActive = async () => {
    if (!editingDriver) return;
    
    setEditingDriver(prev => ({
      ...prev,
      isActive: !prev.isActive
    }));
  };

  // Handle calling a driver
  const handleCallDriver = (phoneNumber) => {
    if (phoneNumber) {
      window.location.href = `tel:${phoneNumber}`;
    }
  };

  return (
    <Box mt={2}>
      <Paper elevation={3} sx={{ padding: 2 }}>
        <Typography variant="h6">Drivers</Typography>
        {readOnly && <ViewOnlyNotice />}
        <Divider sx={{ my: 2 }} />

        {error && (
          <Typography 
            color={error.severity === 'success' ? 'success.main' : 'error.main'} 
            variant="body2"
            sx={{ mb: 2 }}
          >
            {typeof error === 'string' ? error : error.message}
          </Typography>
        )}

        {/* Drivers Table Display - Use drivers prop */}
        {/* Removed top-level loading check as data comes from props */}
        <>
          {drivers.length === 0 ? (
            <Typography variant="body1" sx={{ textAlign: 'center', my: 4 }}>
              No drivers found matching your criteria.
            </Typography>
          ) : (
            <React.Fragment> {/* Use React.Fragment */}
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Primary Role</TableCell>
                      <TableCell>Pay rate</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>On Duty</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Active Drivers Section - Use drivers prop */}
                    {drivers // Use drivers prop directly
                      .filter(driver => driver.isActive !== false)
                      .map((driver) => (
                        <TableRow key={driver.id || driver._id}>
                          <TableCell>{`${driver.firstName || driver.ownerFirstName || ''} ${driver.lastName || driver.ownerLastName || ''}`}</TableCell>
                          <TableCell>{driver.phone}</TableCell>
                          <TableCell>{driver.email}</TableCell>
                          <TableCell>{driver.primaryRole || driver.role}</TableCell>
                          <TableCell>{describeDriverPay(driver.driverPay)}</TableCell>
                          <TableCell>
                            <Box sx={{
                              backgroundColor: driver.isActive !== false ? 'success.light' : 'error.light',
                              borderRadius: 1,
                              px: 1,
                              py: 0.5,
                              display: 'inline-block',
                              color: '#fff'
                            }}>
                              {driver.isActive !== false ? 'Active' : 'Inactive'}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Switch
                              checked={driver.isOnDuty || false}
                              onChange={() => handleToggleOnDuty(driver.id || driver._id)}
                              disabled={loading || readOnly || !canChangeDutyStatus(getStoredPermissions()?.permissions, driver)}
                            />
                          </TableCell>
                          <TableCell align="right">
                            {!readOnly && (
                            <IconButton
                              edge="end"
                              aria-label="edit"
                              onClick={() => handleEditDriver(driver)}
                              color="primary"
                              sx={{ mr: 1 }}
                            >
                              <EditIcon />
                            </IconButton>
                            )}
                            {driver.phone && (
                              <IconButton
                                edge="end"
                                aria-label="call"
                                onClick={() => handleCallDriver(driver.phone)}
                                color="primary"
                              >
                                <PhoneIcon />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    
                    {/* Separator for Inactive Drivers - Use drivers prop */}
                    {drivers.some(driver => driver.isActive === false) && (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ py: 2 }}>
                          <Typography variant="h6" color="textSecondary" align="center">
                            Inactive Drivers
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                      
                    {/* Inactive Drivers Section - Use drivers prop */}
                    {drivers // Use drivers prop directly
                      .filter(driver => driver.isActive === false)
                      .map((driver) => (
                        <TableRow
                          key={driver.id || driver._id}
                          sx={{ backgroundColor: '#f5f5f5' }}
                        >
                          <TableCell>{`${driver.firstName || driver.ownerFirstName || ''} ${driver.lastName || driver.ownerLastName || ''}`}</TableCell>
                          <TableCell>{driver.phone}</TableCell>
                          <TableCell>{driver.email}</TableCell>
                          <TableCell>{driver.primaryRole || driver.role}</TableCell>
                          <TableCell>{describeDriverPay(driver.driverPay)}</TableCell>
                          <TableCell>
                            <Box sx={{
                              backgroundColor: 'error.light',
                              borderRadius: 1,
                              px: 1,
                              py: 0.5,
                              display: 'inline-block',
                              color: '#fff'
                            }}>
                              Inactive
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Switch
                              checked={driver.isOnDuty || false}
                              disabled={true}
                            />
                          </TableCell>
                          <TableCell align="right">
                            {driver.phone && (
                              <IconButton
                                edge="end"
                                aria-label="call"
                                onClick={() => handleCallDriver(driver.phone)}
                                color="primary"
                              >
                                <PhoneIcon />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
                {/* Removed extra </Table> tag */}
              </TableContainer>
              {/* Add TablePagination */}
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(event, newPage) => onPaginationChange(newPage, rowsPerPage)}
                onRowsPerPageChange={(event) => {
                  const newRowsPerPage = parseInt(event.target.value, 10);
                  onPaginationChange(0, newRowsPerPage); // Reset to page 0 on rows per page change
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </React.Fragment>
          )}
        </> {/* Close Outer Fragment */}
      </Paper>

      {/* On-Duty Confirmation Dialog */}
      <Dialog
        open={openOnDutyConfirmDialog}
        onClose={cancelOnDutyChange}
      >
        <DialogTitle>Confirm On-Duty Status Change</DialogTitle>
        <DialogContent>
          {pendingOnDutyChange && (
            <Box> {/* Changed Typography to Box */}
              <Typography> {/* Main question */}
                Are you sure you want to set {pendingOnDutyChange.driver.firstName || pendingOnDutyChange.driver.ownerFirstName} {pendingOnDutyChange.driver.lastName || pendingOnDutyChange.driver.ownerLastName}
                as {pendingOnDutyChange.newValue ? 'On-Duty' : 'Off-Duty'}?
              </Typography>
              {pendingOnDutyChange.newValue && (
                <Typography sx={{ mt: 1 }}> {/* Explanation */}
                  This driver will now be available for job assignments.
                </Typography>
              )}
              {!pendingOnDutyChange.newValue && (
                <Typography sx={{ mt: 1 }}> {/* Explanation */}
                  This driver will no longer be available for job assignments.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelOnDutyChange}>Cancel</Button>
          <Button onClick={confirmOnDutyChange} color="primary" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Driver Edit Dialog */}
      <Dialog 
        open={openEditDialog} 
        onClose={() => setOpenEditDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Edit Driver Profile
          {editingDriver && (
            <Typography variant="caption" display="block" color="text.secondary">
              {editingDriver.firstName} {editingDriver.lastName}
            </Typography>
          )}
        </DialogTitle>

        <DialogContent>
          {editingDriver && (
            <Box component="form" noValidate autoComplete="off" sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                {/* Account Information Section */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Account Information
                  </Typography>
                </Grid>

                {/* Username */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Username"
                    value={editingDriver.username || ''}
                    onChange={(e) => handleDriverFormChange('username', e.target.value)}
                  />
                </Grid>

                {/* Pay rate: Org 1 admins for Org 1 drivers (weekly ACH run pays by it);
                    SP owners/admins for their own drivers. Backend strips it from anyone else. */}
                {(() => {
                  const pay = normalizeDriverPay(editingDriver.driverPay);
                  const canEdit = canSetDriverPay(getStoredPermissions()?.permissions, editingDriver);
                  const setPay = (patch) => handleDriverFormChange('driverPay', { ...pay, ...patch });
                  return (
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Pay rate</Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        {editingDriver.organizationLevel === 1 ? 'Used by the weekly ACH payout for this driver.' : `Your company's record of what it pays this driver; ${orgName} pays the company the job amount.`}
                        {editingDriver.driverPay?.updatedAt ? ` Last set ${new Date(editingDriver.driverPay.updatedAt).toLocaleDateString()}${editingDriver.driverPay.updatedBy ? ` by ${editingDriver.driverPay.updatedBy}` : ''}.` : ''}
                        {!canEdit ? ' You do not have permission to change it.' : ''}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <FormControl size="small" sx={{ minWidth: 200 }} disabled={!canEdit}>
                          <InputLabel>Rate type</InputLabel>
                          <Select label="Rate type" value={pay.type} onChange={(e) => setPay({ type: e.target.value })}>
                            {PAY_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                          </Select>
                        </FormControl>
                        {pay.type === 'percent' && <TextField size="small" type="number" label="Percent of job" value={pay.percent} onChange={(e) => setPay({ percent: e.target.value })} inputProps={{ min: 0, max: 100, step: 0.5 }} disabled={!canEdit} sx={{ width: 150 }} InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }} />}
                        {pay.type === 'flat' && <TextField size="small" type="number" label="Per job" value={pay.flatAmount} onChange={(e) => setPay({ flatAmount: e.target.value })} inputProps={{ min: 0, step: 0.01 }} disabled={!canEdit} sx={{ width: 150 }} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />}
                        <TextField size="small" label="Note (optional)" value={pay.note} onChange={(e) => setPay({ note: e.target.value })} inputProps={{ maxLength: 200 }} disabled={!canEdit} sx={{ flex: '1 1 200px' }} />
                      </Box>
                    </Grid>
                  );
                })()}

                {/* Password Section */}
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={changePassword}
                        onChange={(e) => setChangePassword(e.target.checked)}
                      />
                    }
                    label="Change Password"
                  />
                </Grid>

                {changePassword && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type={showPassword ? 'text' : 'password'}
                      label="New Password"
                      value={editingDriver.password || ''}
                      onChange={(e) => handleDriverFormChange('password', e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                )}

                {/* Assigned Vehicle */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                    Assigned Vehicle
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="vehicle-select-label">Assigned Vehicle</InputLabel>
                    <Select
                      labelId="vehicle-select-label"
                      value={editingDriver.assignedVehicle || ''}
                      onChange={(e) => handleDriverFormChange('assignedVehicle', e.target.value)}
                      label="Assigned Vehicle"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {vehicles.map((vehicle, index) => (
                        <MenuItem key={vehicle.id || `vehicle-${index}`} value={vehicle.name || vehicle.id}>
                          {vehicle.name} {vehicle.type ? `(${vehicle.type})` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Address Section */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                    Address
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Street"
                    value={(editingDriver.address && editingDriver.address.street1) || ''}
                    onChange={(e) => handleAddressChange('street1', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="City"
                    value={(editingDriver.address && editingDriver.address.city) || ''}
                    onChange={(e) => handleAddressChange('city', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="State"
                    value={(editingDriver.address && editingDriver.address.state) || ''}
                    onChange={(e) => handleAddressChange('state', e.target.value)}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Zip Code"
                    value={(editingDriver.address && editingDriver.address.zip) || ''}
                    onChange={(e) => handleAddressChange('zip', e.target.value)}
                  />
                </Grid>

                {/* Notification Preferences */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                    Notification Preferences
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editingDriver.notifyOnJobAssignment || false}
                          onChange={(e) => handleDriverFormChange('notifyOnJobAssignment', e.target.checked)}
                        />
                      }
                      label="Receive notifications when assigned to a job"
                    />
                    
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={editingDriver.notifyOnStatusChange || false}
                          onChange={(e) => handleDriverFormChange('notifyOnStatusChange', e.target.checked)}
                        />
                      }
                      label="Receive notifications on job status changes"
                    />
                  </FormGroup>
                </Grid>
                
                {/* Status Change Notification Types */}
                {editingDriver.notifyOnStatusChange && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Receive notifications for these status changes:
                    </Typography>
                    <FormGroup row>
                      {['Dispatched', 'En Route', 'On Scene', 'Complete', 'Cancelled', 'GOA'].map((status) => (
                        <FormControlLabel
                          key={status}
                          control={
                            <Checkbox
                              checked={(editingDriver.statusChangeNotificationTypes || []).includes(status)}
                              onChange={(e) => handleNotificationTypeChange(status, e.target.checked)}
                            />
                          }
                          label={status}
                        />
                      ))}
                    </FormGroup>
                  </Grid>
                )}

                {/* Active Status */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                    Driver Status
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editingDriver.isActive !== false}
                        onChange={handleToggleActive}
                      />
                    }
                    label={editingDriver.isActive !== false ? "Active" : "Inactive"}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveDriver} 
            variant="contained" 
            color="primary"
            disabled={saveLoading}
          >
            {saveLoading ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Drivers;
